import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, Warehouse, TrendingUp, AlertTriangle, Truck, Clock, Activity, QrCode } from 'lucide-react';
import { API } from '@/config';

const WarehousePage = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/warehouse/dashboard`);
      setDashboardData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-gray-600">Loading warehouse data...</div>
      </div>
    );
  }

  const zones = dashboardData?.utilization_by_zone || {};
  const todayActivity = dashboardData?.today_activity || {};
  const recentActivity = dashboardData?.recent_activity || [];
  const alerts = dashboardData?.alerts || {};

  // Calculate overall utilization
  const totalCapacity = Object.values(zones).reduce((sum, zone) => sum + zone.capacity, 0);
  const totalOccupied = Object.values(zones).reduce((sum, zone) => sum + zone.occupied, 0);
  const overallUtilization = totalCapacity > 0 ? (totalOccupied / totalCapacity * 100) : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Warehouse className="w-8 h-8" />
            Warehouse Dashboard
          </h1>
          <p className="text-gray-600 mt-1">Real-time warehouse operations and storage management</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/warehouse/scanner')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <QrCode className="w-5 h-5" />
            Scan LP
          </button>
          <div className="text-right">
            <div className="text-sm text-gray-500">Last Updated</div>
            <div className="text-lg font-semibold">{new Date().toLocaleTimeString()}</div>
          </div>
        </div>
      </div>

      {/* Overall KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Overall Utilization */}
        <button
          onClick={() => navigate('/warehouse/storage-map', { state: { fromDashboard: true } })}
          className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-lg shadow hover:shadow-lg transition-all cursor-pointer text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs opacity-90">Overall Utilization</div>
              <div className="text-2xl font-bold mt-1">{overallUtilization.toFixed(1)}%</div>
              <div className="text-xs opacity-90 mt-1">{totalOccupied} / {totalCapacity} pallets</div>
            </div>
            <TrendingUp className="w-8 h-8 opacity-80" />
          </div>
        </button>

        {/* Trucks at Dock */}
        <button
          onClick={() => navigate('/warehouse/trucks')}
          className="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-lg shadow hover:shadow-lg transition-all cursor-pointer text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs opacity-90">Trucks at Dock</div>
              <div className="text-2xl font-bold mt-1">{dashboardData?.trucks_at_dock || 0}</div>
              <div className="text-xs opacity-90 mt-1">Active deliveries/pickups</div>
            </div>
            <Truck className="w-8 h-8 opacity-80" />
          </div>
        </button>

        {/* Pallets on Hold */}
        <button
          onClick={() => navigate('/warehouse/holds')}
          className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow hover:shadow-lg transition-all cursor-pointer text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs opacity-90">Pallets on Hold</div>
              <div className="text-2xl font-bold mt-1">{dashboardData?.pallets_on_hold || 0}</div>
              <div className="text-xs opacity-90 mt-1">Awaiting QA release</div>
            </div>
            <AlertTriangle className="w-8 h-8 opacity-80" />
          </div>
        </button>

        {/* Today's Transactions */}
        <button
          onClick={() => navigate('/warehouse/transactions')}
          className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4 rounded-lg shadow hover:shadow-lg transition-all cursor-pointer text-left"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs opacity-90">Today's Transactions</div>
              <div className="text-2xl font-bold mt-1">{todayActivity.total_transactions || 0}</div>
              <div className="text-xs opacity-90 mt-1">All movements</div>
            </div>
            <Activity className="w-8 h-8 opacity-80" />
          </div>
        </button>
      </div>

      {/* Storage Utilization by Zone */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Package className="w-5 h-5" />
          Storage Utilization by Zone
        </h2>
        <div className="space-y-4">
          {Object.entries(zones).map(([zoneName, data]) => {
            const utilizationPct = data.utilization_pct || 0;
            let colorClass = 'bg-green-500';
            if (utilizationPct > 90) colorClass = 'bg-red-500';
            else if (utilizationPct >= 70) colorClass = 'bg-orange-500';

            return (
              <div key={zoneName} className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="font-semibold">{zoneName}</div>
                  <div className="text-sm text-gray-600">
                    {data.occupied} / {data.capacity} ({utilizationPct.toFixed(1)}%)
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-full ${colorClass} transition-all duration-500`}
                    style={{ width: `${Math.min(utilizationPct, 100)}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Today's Activity
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/warehouse/receipts')}
              className="border rounded-lg p-4 text-center hover:bg-gray-50 transition-colors"
            >
              <div className="text-3xl font-bold text-blue-600">{todayActivity.receipts || 0}</div>
              <div className="text-sm text-gray-600 mt-1">Receipts</div>
            </button>
            <button
              onClick={() => navigate('/warehouse/transactions?type=putaway')}
              className="border rounded-lg p-4 text-center hover:bg-gray-50 transition-colors"
            >
              <div className="text-3xl font-bold text-green-600">{todayActivity.putaways || 0}</div>
              <div className="text-sm text-gray-600 mt-1">Putaways</div>
            </button>
            <button
              onClick={() => navigate('/warehouse/transactions?type=pick')}
              className="border rounded-lg p-4 text-center hover:bg-gray-50 transition-colors"
            >
              <div className="text-3xl font-bold text-purple-600">{todayActivity.picks || 0}</div>
              <div className="text-sm text-gray-600 mt-1">Picks</div>
            </button>
            <button
              onClick={() => navigate('/warehouse/transactions?type=shipment')}
              className="border rounded-lg p-4 text-center hover:bg-gray-50 transition-colors"
            >
              <div className="text-3xl font-bold text-orange-600">{todayActivity.shipments || 0}</div>
              <div className="text-sm text-gray-600 mt-1">Shipments</div>
            </button>
          </div>
        </div>

        {/* Alerts & Issues */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Alerts & Issues
          </h2>
          <div className="space-y-3">
            {alerts.holds > 0 && (
              <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                <div>
                  <div className="font-semibold text-orange-900">{alerts.holds} Pallets on Hold</div>
                  <div className="text-sm text-orange-700">Awaiting QA release</div>
                </div>
              </div>
            )}
            
            {alerts.expiring_soon > 0 && (
              <div className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <div>
                  <div className="font-semibold text-yellow-900">{alerts.expiring_soon} Items Expiring Soon</div>
                  <div className="text-sm text-yellow-700">Within next 30 days</div>
                </div>
              </div>
            )}

            {alerts.holds === 0 && alerts.expiring_soon === 0 && (
              <div className="text-center text-gray-500 py-8">
                <div className="text-4xl mb-2">✓</div>
                <div>No active alerts</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity Feed */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Recent Activity
        </h2>
        {recentActivity.length > 0 ? (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {recentActivity.map((activity, index) => {
              const time = new Date(activity.timestamp).toLocaleTimeString();
              let icon = <Package className="w-4 h-4" />;
              let bgColor = 'bg-blue-100';
              let textColor = 'text-blue-700';

              if (activity.transaction_type === 'receipt') {
                bgColor = 'bg-green-100';
                textColor = 'text-green-700';
              } else if (activity.transaction_type === 'pick') {
                bgColor = 'bg-purple-100';
                textColor = 'text-purple-700';
              } else if (activity.transaction_type === 'shipment') {
                bgColor = 'bg-orange-100';
                textColor = 'text-orange-700';
              }

              return (
                <div key={index} className="flex items-center gap-3 p-3 border-b hover:bg-gray-50">
                  <div className={`p-2 rounded ${bgColor} ${textColor}`}>
                    {icon}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold capitalize">{activity.transaction_type}</div>
                    <div className="text-sm text-gray-600">
                      {activity.item_name} - {activity.quantity} {activity.uom}
                      {activity.lot_number && ` | Lot: ${activity.lot_number}`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">{time}</div>
                    <div className="text-xs text-gray-400">{activity.user}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            No recent activity
          </div>
        )}
      </div>
    </div>
  );
};

export default WarehousePage;
